
/**
 * WeakMap-based cache for proxy instances.
 * Key: target object, Value: Map of path -> proxy
 *
 * Provides 90%+ reduction in proxy creation for nested objects.
 * Memory is automatically managed - entries are GC'd when source objects are collected.
 *
 * @type {WeakMap<object, Map<string, Proxy>>}
 */
const proxyCache = new WeakMap();

/**
 * Reverse lookup: proxy -> raw target.
 *
 * Lets internal hot paths (notably the deep Publisher) walk
 * the underlying object directly instead of triggering the
 * proxy get trap on every nested access. Without this, a
 * single deep publish can re-allocate or re-look-up dozens
 * of nested proxies per leaf.
 *
 * @type {WeakMap<object, object>}
 */
const _proxyTargets = new WeakMap();

/**
 * Return the underlying target object for a proxy created
 * by this module, or the value itself if it is not one of
 * our proxies. Safe to call with any value.
 *
 * @param {*} value
 * @returns {*}
 */
export function unwrapProxy(value)
{
	if (!value || typeof value !== 'object')
	{
		return value;
	}
	const raw = _proxyTargets.get(value);
	return raw !== undefined ? raw : value;
}

/**
 * Per-target cache for bound method references.
 * Every access to a method through the proxy (e.g. data.set) would
 * otherwise call value.bind(target) and allocate a new function object.
 * This cache makes subsequent accesses a Map lookup (~10ns) instead.
 *
 * @type {WeakMap<object, Map<string, Function>>}
 */
const _boundMethodCache = new WeakMap();

/**
 * Get or create a cached proxy for a target object at a specific path.
 *
 * @param {object} target - The object to proxy
 * @param {object} data - The data instance
 * @param {string} path - The path string
 * @param {string} dataRoot - The data root property
 * @returns {Proxy}
 */
function getCachedProxy(target, data, path, dataRoot)
{
	// Get or create the path cache for this target
	let targetCache = proxyCache.get(target);
	if (!targetCache)
	{
		targetCache = new Map();
		proxyCache.set(target, targetCache);
	}

	// Check if we already have a proxy for this path
	const cachedProxy = targetCache.get(path);
	if (cachedProxy)
	{
		return cachedProxy;
	}

	// Create new proxy and cache it
	const proxy = new Proxy(target, createHandler(data, path, dataRoot));
	targetCache.set(path, proxy);
	_proxyTargets.set(proxy, target);

	return proxy;
}

/**
 * Invalidate cached proxies for a specific target object.
 * Called when an object is replaced to ensure stale proxies aren't used.
 *
 * @param {object} target - The object whose cache should be cleared
 * @returns {void}
 */
export function invalidateProxyCache(target)
{
	if (target && typeof target === 'object')
	{
		proxyCache.delete(target);
	}
}

/**
 * This will get hte path of the prop.
 *
 * @param {string} path
 * @param {string} prop
 * @returns {string}
 */
function getNewPath(path, prop)
{
	/* Digit check (charCode 48='0' to 57='9') avoids
	   isNaN(Number(prop)) which converts to a heap Number. */
	const c = prop.charCodeAt(0);
	const isIndex = c >= 48 && c <= 57;
	if (path === '')
	{
		return isIndex ? '[' + prop + ']' : prop;
	}

	return isIndex ? path + '[' + prop + ']' : path + '.' + prop;
}

/**
 * This will create a handler for the proxy.
 *
 * @param {object} data
 * @param {string} path
 * @param {string} dataRoot
 * @returns {ProxyHandler<any>}
 */
function createHandler(data, path = '', dataRoot = '')
{
	return {

		/**
		 * This will get the value of the prop.
		 *
		 * @param {object} target
		 * @param {string} prop
		 * @param {object} receiver
		 * @returns {*}
		 */
		// @ts-ignore
		get(target, prop, receiver)
		{
			let value = target[prop];
			// Directly return the property if it's on the root level and we're at the root path
			if (path === '' && prop in target)
			{
				if (typeof value === 'function')
				{
					/* Cache bound methods per target to avoid allocating a new
					 * bound function on every method access (e.g. data.set, data.get). */
					let methodCache = _boundMethodCache.get(target);
					if (!methodCache)
					{
						methodCache = new Map();
						_boundMethodCache.set(target, methodCache);
					}

					const cached = methodCache.get(prop);
					if (cached) return cached;

					const bound = value.bind(target);
					methodCache.set(prop, bound);
					return bound;
				}

				return value;
			}

			// Access the property within the dataRoot
			const dataTarget = target[dataRoot] || target;
			/* Direct bracket access is faster than Reflect.get for plain data
			 * objects where we do not need the receiver binding. */
			value = dataTarget[prop];

			// Return the value directly if it's not an object
			if (value !== null && typeof value === 'object')
			{
				// Create a new handler for nested properties
				const newPath = getNewPath(path, prop);
				// Use cached proxy instead of creating new one every time (90%+ reduction)
				// @ts-ignore
				return getCachedProxy(value, data, newPath, dataRoot);
			}

		   return value;
		},

		/**
		 * This will set the value of the prop.
		 *
		 * @param {object} target
		 * @param {string} prop
		 * @param {*} value
		 * @param {object} receiver
		 * @returns {*}
		 */
		set(target, prop, value, receiver)
		{
			// Set the property at the root level if we're at the root path
			if (path === '' && prop in target)
			{
				target[prop] = value;
				return true;
			}

			const newPath = getNewPath(path, prop);
			data.set(newPath, value);

			return true;
		}
	};
}

/**
 * This will create a data proxy.
 *
 * @param {object} data
 * @returns {Proxy}
 */
// @ts-ignore
export const DataProxy = (data, root = 'stage') =>
{
	const proxy = new Proxy(data, createHandler(data, '', root));
	_proxyTargets.set(proxy, data);
	return proxy;
};